import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  Pagination,
} from '@mui/material';
import { Add, Edit, Delete, Search as SearchIcon } from '@mui/icons-material';
import SwalWithA11yFix from '../../services/swalConfig';
import api from '../../services/api';

const LocationManagement = () => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const itemsPerPage = 10;

  // Filter locations based on search query
  const filteredLocations = locations.filter((location) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      location.name?.toLowerCase().includes(query) ||
      location.description?.toLowerCase().includes(query)
    );
  });

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/locations');
      setLocations(response.data);
    } catch (error) {
      console.error('Error fetching locations:', error);
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch locations',
        confirmButtonColor: '#1976d2',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (location = null) => {
    if (location) {
      setSelectedLocation(location);
      setFormData({
        name: location.name,
        description: location.description || '',
      });
    } else {
      setSelectedLocation(null);
      setFormData({
        name: '',
        description: '',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedLocation(null);
    setFormData({
      name: '',
      description: '',
    });
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!formData.name.trim()) {
      return;
    }
    try {
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
      };
      if (selectedLocation) {
        await api.put(`/locations/${selectedLocation._id}`, submitData);
        SwalWithA11yFix({
          icon: 'success',
          title: 'Success!',
          text: 'Location updated successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        await api.post('/locations', submitData);
        SwalWithA11yFix({
          icon: 'success',
          title: 'Success!',
          text: 'Location created successfully',
          timer: 2000,
          showConfirmButton: false,
        });
      }
      handleCloseDialog();
      fetchLocations();
    } catch (err) {
      // Don't show alert for validation errors (400 status) as field-level validation handles them
      if (err.response?.status !== 400) {
        SwalWithA11yFix({
          icon: 'error',
          title: 'Error',
          text: err.response?.data?.message || 'Operation failed',
          confirmButtonColor: '#1976d2',
        });
      }
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/locations/${selectedLocation._id}`);
      SwalWithA11yFix({
        icon: 'success',
        title: 'Success!',
        text: 'Location deleted successfully',
        timer: 2000,
        showConfirmButton: false,
      });
      setDeleteDialogOpen(false);
      setSelectedLocation(null);
      fetchLocations();
    } catch (err) {
      SwalWithA11yFix({
        icon: 'error',
        title: 'Error',
        text: err.response?.data?.message || 'Delete failed',
        confirmButtonColor: '#1976d2',
      });
    }
  };

  return (
    <Box>
       <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'stretch', gap: 2, width: { xs: '100%', sm: 'auto' } }}>
          <TextField
            placeholder="Search locations..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#999' }} />,
            }}
            sx={{
              width: { xs: '100%', sm: 250 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
              },
            }}
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              whiteSpace: 'nowrap',
              '&:hover': {
                backgroundColor: '#1565c0',
                boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
              },
            }}
          >
            Add Location
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          maxWidth: '100%',
          overflowX: 'auto',
          backgroundColor: 'white',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredLocations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="textSecondary">
                      {searchQuery ? 'No locations match your search' : 'No locations found'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredLocations.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((location) => (
                <TableRow key={location._id}>
                  <TableCell>{location.name}</TableCell>
                  <TableCell>{location.description || '-'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(location)}>
                      <Edit />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedLocation(location);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {/* Pagination */}
      {!loading && locations.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={Math.ceil(filteredLocations.length / itemsPerPage)}
            page={page}
            onChange={(event, value) => setPage(value)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1976d2' }}>
          {selectedLocation ? 'Edit Location' : 'Create Location'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              error={!formData.name.trim() && submitted}
              helperText={!formData.name.trim() && submitted ? 'Location name is required' : ''}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              backgroundColor: '#1976d2',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            {selectedLocation ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        disableEnforceFocus
        disableRestoreFocus
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1976d2' }}>Delete Location</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedLocation?.name}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            sx={{
              backgroundColor: '#d32f2f',
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: '#c62828',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LocationManagement;
